export const sanitizeProgramsForSave = (programs) => {
  console.log("🧹 sanitizeProgramsForSave called with:", {
    isArray: Array.isArray(programs),
    length: programs?.length,
    type: typeof programs,
    firstItem: programs?.[0],
    firstItemKeys: programs?.[0] ? Object.keys(programs[0]) : [],
    callStack: new Error().stack.split('\n').slice(1, 4).join('\n')
  });

  if (!programs || !Array.isArray(programs) || programs.length === 0) {
    console.error("❌ CRITICAL: No programs to sanitize - returning empty array");
    console.error("❌ This is the bug causing empty saves!");
    console.error("❌ Input was:", programs);
    console.error("❌ Call stack:", new Error().stack);
    return [];
  }

  if (programs.length > 100) {
    console.error(
      "MASSIVE CORRUPTION DETECTED: " + programs.length + " programs - rejecting to prevent crash",
    );
    return [];
  }

  console.log("Frontend sanitizing " + programs.length + " programs");

  const seenNames = new Set();
  const deduplicatedPrograms = programs.filter((program) => {
    const name = program?.program_of_work || program?.name || program?.title;
    if (!name || seenNames.has(name)) {
      console.log("Removing duplicate program: " + name);
      return false;
    }
    seenNames.add(name);
    return true;
  });

  console.log(
    "After deduplication: " + deduplicatedPrograms.length + " programs",
  );

  const limitedPrograms = deduplicatedPrograms.slice(0, 10);

  const sanitizedPrograms = limitedPrograms
    .map((program, index) => {
      if (!program || typeof program !== "object") {
        console.warn("Invalid program object at index " + index + ", skipping");
        return null;
      }

      const cleanProgram = {
        program_of_work: cleanCorruptedText(String(
          program.program_of_work ||
            program.name ||
            program.title ||
            "Unnamed Program",
        )).substring(0, 100),
        phase: cleanCorruptedText(String(program.phase || "Discovery")).substring(0, 30),
        goal: cleanCorruptedText(String(program.goal || program.outcome || "")).substring(0, 500),
        description: cleanCorruptedText(String(program.description || "")).substring(0, 500),
        totalProgramLength: Math.min(
          Number(program.totalProgramLength) ||
            Number(program.duration) ||
            40,
          1000,
        ),
        isIncluded: program.hasOwnProperty('isIncluded') ? Boolean(program.isIncluded) : true, // Preserve the exact isIncluded state from user input

        methods: Array.isArray(program.methods)
          ? program.methods
              .slice(0, 5)
              .map((m) => cleanCorruptedText(String(m)).substring(0, 100))
          : Array.isArray(program.related_methods)
            ? program.related_methods
                .slice(0, 5)
                .map((m) => cleanCorruptedText(String(m)).substring(0, 100))
            : [],

        Stages: Array.isArray(program.Stages)
          ? program.Stages.slice(0, 5).map((s) => cleanCorruptedText(String(s)).substring(0, 100))
          : Array.isArray(program.stages)
            ? program.stages
                .slice(0, 5)
                .map((s) => cleanCorruptedText(String(s)).substring(0, 100))
            : [],

        focus: Array.isArray(program.Focus)
          ? program.Focus.slice(0, 3).map((f) => cleanCorruptedText(String(f)).substring(0, 100))
          : Array.isArray(program.focus)
            ? program.focus
                .slice(0, 3)
                .map((f) => cleanCorruptedText(String(f)).substring(0, 100))
            : [],

        expectedDeliverables: Array.isArray(program.expectedDeliverables)
          ? program.expectedDeliverables
              .slice(0, 5)
              .map((d) => cleanCorruptedText(String(d)).substring(0, 200))
          : [],

        toolsByStage:
          program.toolsByStage && typeof program.toolsByStage === "object"
            ? Object.keys(program.toolsByStage).reduce(
                (optimizedStages, stageName) => {
                  const stage = program.toolsByStage[stageName];
                  if (stage && typeof stage === "object") {
                    optimizedStages[String(stageName)] = {
                      tools: Array.isArray(stage.tools)
                        ? stage.tools.map((tool) => ({
                            id:
                              tool.id ||
                              `tool-${Date.now()}-${Math.random()}`,
                            tool: cleanCorruptedText(tool.tool || tool.name || "Unknown Tool"),
                            description: cleanCorruptedText(tool.description || ""),
                            duration: Number(tool.duration) || 1,
                            related_stage: tool.related_stage || stageName,
                            complexity: tool.complexity || "simple",
                            related_methods: Array.isArray(
                              tool.related_methods,
                            )
                              ? tool.related_methods
                              : typeof tool.related_methods === "string"
                                ? tool.related_methods
                                    .split(",")
                                    .map((m) => m.trim())
                                : [],
                          }))
                        : [],
                      totalDuration: Number(stage.totalDuration) || 0,
                    };
                  }
                  return optimizedStages;
                },
                {},
              )
            : {},

        score: Number(program.score) || 0,
        similarity_score: Number(program.similarity_score) || 0,
      };

      const jsonSize = JSON.stringify(cleanProgram).length;
      if (jsonSize > 8000) {
        console.warn(
          "Program " + index + " too large (" + jsonSize + " chars), truncating descriptions only",
        );
        cleanProgram.description = cleanProgram.description.substring(0, 100);
        cleanProgram.goal = cleanProgram.goal.substring(0, 200);

        if (JSON.stringify(cleanProgram).length > 8000) {
          Object.keys(cleanProgram.toolsByStage).forEach((stageName) => {
            if (cleanProgram.toolsByStage[stageName].tools) {
              cleanProgram.toolsByStage[stageName].tools =
                cleanProgram.toolsByStage[stageName].tools.slice(0, 5);
            }
          });
        }
      }

      console.log("Sanitized program " + index + ":", {
        name: cleanProgram.program_of_work,
        phase: cleanProgram.phase,
        duration: cleanProgram.totalProgramLength,
        methodsCount: cleanProgram.methods.length,
        stagesCount: cleanProgram.Stages.length,
        toolStagesCount: Object.keys(cleanProgram.toolsByStage).length,
        size: JSON.stringify(cleanProgram).length,
      });

      return cleanProgram;
    })
    .filter(Boolean);

  console.log("Sanitization complete:", {
    inputCount: programs.length,
    outputCount: sanitizedPrograms.length,
    totalSize: JSON.stringify(sanitizedPrograms).length,
  });

  return sanitizedPrograms;
};

export const calculateProgramLengthAndStages = (tools) => {
  const stages = {};
  tools.forEach((tool) => {
    const stage = tool.Stage || "Unknown";
    if (!stages[stage]) {
      stages[stage] = { tools: [], totalDuration: 0 };
    }
    stages[stage].tools.push(tool);
    stages[stage].totalDuration += tool.duration || 0;
  });
  return stages;
};

export const extractToolsFromProgram = (program) => {
  const tools = [];
  if (program.toolsByStage && typeof program.toolsByStage === 'object') {
    Object.keys(program.toolsByStage).forEach((stageName) => {
      const stage = program.toolsByStage[stageName];
      if (stage && Array.isArray(stage.tools)) {
        stage.tools.forEach((tool) => {
          tools.push({
            ...tool,
            stage: stageName,
          });
        });
      }
    });
  }
  return tools;
};

export const formatDuration = (hours) => {
  const weeks = Math.floor(hours / 40);
  const days = Math.floor((hours % 40) / 8);
  const remainingHours = hours % 8;

  let result = "";
  if (weeks) result += weeks + " week" + (weeks > 1 ? "s" : "") + ", ";
  if (days) result += days + " day" + (days > 1 ? "s" : "") + ", ";
  if (remainingHours || (!weeks && !days))
    result += remainingHours + " hour" + (remainingHours > 1 ? "s" : "");
  return result.trim().replace(/,\s*$/, "");
};

export const cleanCorruptedText = (text) => {
  if (!text || typeof text !== 'string') return text;

  return text
    .replace(/\\{3,}"/g, '"')     // Fix multiple escaped quotes
    .replace(/\\{2,}/g, '')       // Remove multiple consecutive backslashes  
    .replace(/\\"/g, '"')         // Unescape quotes
    .replace(/\\n/g, '\n')        // Unescape newlines
    .replace(/\\t/g, '\t')        // Unescape tabs
    .replace(/\\\\/g, '\\')       // Fix double-escaped backslashes
    .replace(/\\'/g, "'")         // Unescape single quotes
    .replace(/\\r/g, '\r')        // Unescape carriage returns
    .replace(/\["/g, '[')         // Remove quotes after array brackets
    .replace(/"\]/g, ']')         // Remove quotes before array brackets
    .replace(/","\s*-\s*/g, '\n') // Fix comma-quote-dash sequences for objectives
    .replace(/","/g, '\n')        // Convert comma-quote sequences to newlines
    .replace(/^\["|"\]$/g, '')    // Remove array bracket quotes from start/end
    .replace(/^"+|"+$/g, '')      // Remove leading/trailing quotes
    .replace(/^\[|\]$/g, '')      // Remove array brackets from objectives text
    .trim();
};

export const cleanJSONString = (jsonString) => {
  if (!jsonString || typeof jsonString !== 'string') return jsonString;

  // Remove excessive escaping that accumulates over save/load cycles
  return jsonString
    .replace(/\\{3,}"/g, '"')     // Fix multiple escaped quotes
    .replace(/\\{2,}n/g, '\\n')   // Fix multiple escaped newlines
    .replace(/\\{2,}t/g, '\\t')   // Fix multiple escaped tabs
    .replace(/\\{2,}r/g, '\\r')   // Fix multiple escaped returns
    .replace(/\\{4,}/g, '\\');    // Fix excessive backslashes
};